import * as React from 'react';

import { MultiSelectFilter, cn, Badge } from '@rematter/ui-web';

import { useCustomerAccounts } from '../../_graphql/queryCustomerAccounts';
import { useTranslations } from '../../i18n/useTranslations';

interface Account {
  id: string;
  name: string;
  type?: string;
  email?: string;
  phone?: string;
  website?: string;
  relationship?: {
    isCustomer: boolean;
    isSupplier: boolean;
  };
}

interface CustomerFilterProps {
  /** Currently selected customer IDs */
  selectedCustomerIds: Set<React.Key>;

  /** Callback when customer selection changes */
  onSelectionChange: (customerIds: Set<React.Key>) => void;

  /** Callback when Reset button is clicked */
  onReset: () => void;

  /** Function to close the filter popover */
  onClose?: () => void;

  /** Additional CSS classes */
  className?: string;
}

export const CustomerFilter: React.FC<CustomerFilterProps> = ({
  selectedCustomerIds,
  onSelectionChange,
  onReset,
  onClose,
  className,
}) => {
  const t = useTranslations();
  const [searchTerm, setSearchTerm] = React.useState<string | undefined>(
    undefined,
  );

  const {
    data: customers,
    isFetching: isFetchingCustomers,
    isLoading: isLoadingCustomers,
  } = useCustomerAccounts({
    searchTerm,
    limit: 20,
  });

  // Fetch selected customers
  const { data: selectedCustomers } = useCustomerAccounts({
    accountIds: Array.from(selectedCustomerIds) as string[],
  });

  // Handle search with debouncing via MultiSelectFilter
  const handleSearch = React.useCallback((query: string) => {
    setSearchTerm(query);
  }, []);

  // Custom item renderer for customers
  const getLabel = React.useCallback(
    (item: Account, isSelected: boolean) => (
      <div className="flex w-full items-center gap-2">
        <div
          className={cn(
            'w-0 grow truncate text-sm text-neutral/60',
            isSelected && 'text-neutral',
          )}
        >
          {item.name}
        </div>
        {item.type && (
          <Badge
            color={item.type === 'Individual' ? 'warning' : 'primary'}
            className="ml-auto"
          >
            {item.type}
          </Badge>
        )}
      </div>
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className={cn('@CustomerFilter min-w-[350px]', className)}
      entries={customers}
      selectedEntries={selectedCustomers}
      selectedKeys={selectedCustomerIds}
      onSelectionChange={onSelectionChange}
      getEntryKey={(customer) => customer.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={onReset}
      searchPlaceholder={t.filters.searchCustomers}
      isLoading={isLoadingCustomers}
      isFetching={isFetchingCustomers}
    />
  );
};
